import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Star, Edit3, Loader2 } from 'lucide-react';
import api from '../../services/api';

const StarRating = ({ rating, onRate, size = 'md', readonly = false }) => {
  const [hovered, setHovered] = useState(0);
  const sz = size === 'sm' ? 'w-4 h-4' : 'w-6 h-6';
  return (
    <div className="flex gap-0.5" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={`review-star-${i}`}
          type="button"
          disabled={readonly}
          onClick={() => onRate?.(i)}
          onMouseEnter={() => !readonly && setHovered(i)}
          onMouseLeave={() => setHovered(0)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          data-testid={`star-${i}`}
        >
          <Star className={`${sz} ${(hovered || rating) >= i ? 'fill-gold text-gold' : 'text-charcoal/20'} transition-colors`} />
        </button>
      ))}
    </div>
  );
};

const TripReviewSection = ({ itineraryId, destinationCity, destinationCountry }) => {
  const [review, setReview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/reviews/my/${itineraryId}`);
        if (res.data.success && res.data.review) {
          setReview(res.data.review);
          setRating(res.data.review.rating);
          setText(res.data.review.text);
        }
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [itineraryId]);

  const handleSubmit = async () => {
    if (!rating || !text.trim()) {
      toast.error('Please provide a rating and review text');
      return;
    }
    setSubmitting(true);
    try {
      if (review) {
        await api.put(`/reviews/${review.id}`, { rating, text: text.trim() });
        setReview(prev => ({ ...prev, rating, text: text.trim() }));
        toast.success('Review updated!');
      } else {
        const res = await api.post('/reviews', {
          itinerary_id: itineraryId,
          rating,
          text: text.trim(),
          destination_city: destinationCity,
          destination_country: destinationCountry || ''
        });
        if (res.data.success) {
          setReview(res.data.review);
          toast.success('Review submitted!');
        }
      }
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save review');
    } finally { setSubmitting(false); }
  };

  if (loading) return null;

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl p-5 shadow-lg" data-testid="trip-review-section">
      <h4 className="font-semibold text-ocean text-sm flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-gold" /> Rate Your Trip
      </h4>

      {review && !editing ? (
        <div className="space-y-2">
          <StarRating rating={review.rating} readonly size="md" />
          <p className="text-charcoal/70 text-sm leading-relaxed">{review.text}</p>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}
            className="text-gold hover:text-gold-dark text-xs h-7 px-2 gap-1" data-testid="edit-review-btn">
            <Edit3 className="w-3 h-3" /> Edit Review
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <StarRating rating={rating} onRate={setRating} />
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="How was your trip? Share your experience..."
            className="w-full bg-ocean/5 border border-ocean/15 rounded-xl px-4 py-3 text-sm text-charcoal placeholder-charcoal/40 resize-none h-24 focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20"
            data-testid="review-text-input"
          />
          <div className="flex items-center gap-2">
            <Button onClick={handleSubmit} disabled={submitting || !rating}
              className="bg-gold hover:bg-gold-dark text-ocean font-semibold text-sm h-9 px-4 rounded-lg" data-testid="submit-review-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {review ? 'Update Review' : 'Submit Review'}
            </Button>
            {editing && (
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setRating(review.rating); setText(review.text); }}
                className="text-charcoal/50 text-sm h-9">Cancel</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { TripReviewSection, StarRating };
